import React, { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    GridValidRowModel,
    GridRowSelectionModel,
    useGridApiRef,
    GridPreferencePanelsValue,
    GridColumnVisibilityModel,
    GridRowParams,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridSortModel,
} from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import store, { IAppReduxStore } from '../../../redux/store';
import DocTableAdditionalDocs from '../DocTable/DocTableAdditionalDocs';
import DocTableError from '../DocTable/DocTableError';
import { createActionColDef, getColumns, processDocuments } from '../DocTable/DocTableUtils';
import StyledDataGrid from '../DocTable/Styled/StyledDataGridPro';
import { useTranslation } from 'react-i18next';
import Dropzone, { resetFnConfig as dropzoneResetConfig } from '../Dropzone/Dropzone';
import { updateSelectedDocuments } from '../../../redux/project/documents/sliceProjectSelectedDocuments';
import {
    setIsHidden as setUploadDialogHidden,
    setIsOpen as setUploadDialogOpen,
} from '../../../redux/import/sliceImportDialog';
import { GlobalVariables } from '../../../GlobalVariables';
import { getFileBasicInfo } from '../../../util/FileUtils';
import { appendFiles } from '../../../redux/import/sliceImportFileUpload';
import UploadDialog from '../Dialogs/UploadDialog/UploadDialog';
import {
    createDragColumn,
    createStatusColumn,
    fetchImportDocuments,
} from '../../../util/ImportUtils';
import { useParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import {
    setHasFilteredItems,
    setShowColumnsMenu,
    setShowFilter,
} from '../../../redux/user/sliceUserSettings';
import DocTableToolbar from '../DocTable/DocTableToolbar';
import DocTableMenu from '../DocTable/DocTableMenu';
import { IDocDefaultFields } from '../../../redux/project/documents/sliceProjectDocuments';
import { getMultilineFields } from '../../../util/DataUtils';

/** Holds the value of the selected document DocType on context menu. */
let selectedDocumentDocType: string;
/** Holds the value of the selected document UniversalID on context menu. */
let selectedDocumentUniversalId: string;

let currentDocument: IDocDefaultFields;

/** Offset in x direction for the context menu */
const offsetX = 2;
/** Offset in y direction for the context menu */
const offsetY = 4;

export default () => {
    const divRef = useRef<HTMLDivElement>();
    const dispatch: Dispatch<unknown> = useDispatch();
    /** Get the replicaId, areaName, viewName and category from the react router. */
    const { replicaId, areaName, category } = useParams();
    const { i18n, t } = useTranslation('translations', { keyPrefix: 'docTable' });

    const docTypes = useSelector((_store: IAppReduxStore) => _store.projectDocTypes.data);
    const showUploadDialog = useSelector((_store: IAppReduxStore) => _store.importDialog.open);

    const importDocuments = useSelector((_store: IAppReduxStore) => _store.importDocuments.data);
    const selectedDocuments = useSelector(
        (_store: IAppReduxStore) => _store.projectSelectedDocuments.storage.documentIds,
    );
    const docsLoading = useSelector((_store: IAppReduxStore) => _store.importDocuments.loading);
    const fields = useSelector((_store: IAppReduxStore) => _store.importViewData.fields.data);
    const fieldSchema = useSelector(
        (_store: IAppReduxStore) => _store.importViewData.fieldSchema.data,
    );
    const allAdditionalDocuments = useSelector(
        (_store: IAppReduxStore) => _store.projectAdditionalDocs.documents.data,
    );
    const documentsToExpandNoteIds = useSelector(
        (_store: IAppReduxStore) => _store.projectAdditionalDocs.documentsToExpandNoteIds,
    );
    const { showFilter, showColumnsMenu } = useSelector(
        (store: IAppReduxStore) => store.userSettings,
    );

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
        {},
    );
    const [initialSorted, setInitialSorted] = useState<boolean>(false);

    const gridApiRef = useGridApiRef();

    useEffect(() => {
        const columnVisibilityModel = fields.reduce((obj, field) => {
            !field.visible && (obj[field.field] = field.visible);
            return obj;
        }, {});
        setColumnVisibilityModel(columnVisibilityModel);
        gridApiRef.current.setColumnVisibilityModel(columnVisibilityModel);
    }, [fields, gridApiRef]);

    useEffect(() => {
        showFilter
            ? gridApiRef.current?.showPreferences(GridPreferencePanelsValue.filters)
            : gridApiRef.current?.hidePreferences();
    }, [showFilter]);

    useEffect(() => {
        showColumnsMenu
            ? gridApiRef.current?.showPreferences(GridPreferencePanelsValue.columns)
            : gridApiRef.current?.hidePreferences();
    }, [showColumnsMenu]);

    useEffect(() => {
        dispatch(setShowFilter(false));
        dispatch(setShowColumnsMenu(false));
        dispatch(setHasFilteredItems(false));
    }, [replicaId, areaName, category]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeydown);

        return () => {
            window.removeEventListener('keydown', handleKeydown);
        };
    }, []);

    const getRows = useCallback((): GridValidRowModel[] => {
        return processDocuments(importDocuments, fields);
    }, [importDocuments, fields]);

    const getCols = useCallback(() => {
        return [
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                hideable: false,
            },
            ...(selectedDocuments?.length > 0 ? [createDragColumn()] : []),
            createActionColDef(fieldSchema, docTypes, t),
            createStatusColumn(),
            ...getColumns(fieldSchema, fields, rows, t).filter(Boolean),
        ];
    }, [fieldSchema, docTypes, fields, selectedDocuments, i18n.language]);

    const initialSortModel = useMemo((): GridSortModel => {
        const initialSortField = fields?.find(x => x.sorted)?.field;
        return initialSortField
            ? [
                  {
                      field: initialSortField,
                      sort: 'asc',
                  },
              ]
            : undefined;
    }, [fields]);

    /**
     * Overrides CTRL+A sequence and Escape key to manipulate data grid row selection.
     */
    const handleKeydown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onSelectionChanged([]);
                return;
            }

            if (e.ctrlKey && e.key === 'a' && importDocuments?.length) {
                e.preventDefault();
                e.stopPropagation();

                onSelectionChanged(importDocuments.map(x => x.NoteId));
            }
        },
        [importDocuments],
    );

    /** Changes the state from the coordinations of the mouse click to false. */
    const [contextMenuPosition, setContextMenuPosition] = React.useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);

    /** Handles the context menu for each individual document row. */
    const handleContextMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();

        setContextMenuPosition({
            mouseX: event.clientX - offsetX,
            mouseY: event.clientY - offsetY,
        });

        const rowId = event.currentTarget.dataset.id;
        const row = rows.find(el => el.NoteId === rowId);

        selectedDocumentDocType = row.DocType || row.docType;
        selectedDocumentUniversalId = row.UniversalID;
        currentDocument = row as IDocDefaultFields;
    };

    const onUploadDialogClose = useCallback(() => {
        fetchImportDocuments(replicaId);

        dispatch(setUploadDialogHidden(false));
        dispatch(setUploadDialogOpen(false));

        dropzoneResetConfig.resetFn?.();
    }, [replicaId]);

    const onSelectionChanged = (rowSelectionModel: GridRowSelectionModel) => {
        const noteIDs = rowSelectionModel.map(x => x.toString());
        const documentIds = noteIDs.map(
            noteID => rows.find(x => x.NoteId === noteID).UniversalID as string,
        );

        dispatch(
            updateSelectedDocuments({
                replicaId,
                areaName,
                documentIds,
            }),
        );
    };

    const onDropFiles = async (droppedFiles: File[]) => {
        if (droppedFiles.length > 0) {
            const fileInfos = await Promise.all(
                droppedFiles.map(async file => {
                    const hash = GlobalVariables.addFile(file);
                    const basicInfo = await getFileBasicInfo(file);
                    return {
                        id: hash,
                        ...basicInfo,
                    };
                }),
            );

            store.dispatch(appendFiles(fileInfos));

            dispatch(setUploadDialogOpen(true));
        }
    };

    const fieldsWithMultilineType = useMemo(() => getMultilineFields(fields), [fields]);

    const rows = getRows();
    const cols = getCols();

    /**
     * Creates the Master Detail area for a given DataGrid row.
     */
    const getAdditionalDocuments = useCallback(
        ({ row }: GridRowParams) => {
            return <DocTableAdditionalDocs row={row} />;
        },
        [cols],
    );

    const expandDetailPanels = () => {
        const rowIds = rows
            .filter(row => documentsToExpandNoteIds.includes(row.NoteId))
            .map(row => row.NoteId);

        gridApiRef.current.setExpandedDetailPanels(rowIds);
    };

    useEffect(() => {
        documentsToExpandNoteIds.length && expandDetailPanels();
    }, [rows, allAdditionalDocuments, documentsToExpandNoteIds]);

    useEffect(() => {
        if (!initialSorted && initialSortModel && importDocuments?.length > 0) {
            gridApiRef.current?.setSortModel(initialSortModel);
            setInitialSorted(true);
        }
    }, [initialSorted, initialSortModel, importDocuments]);

    return (
        <div ref={divRef} style={{ height: '100%', width: '100%' }}>
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <ErrorBoundary fallback={<DocTableError />}>
                    <StyledDataGrid
                        sortingOrder={['asc', 'desc']}
                        slots={{
                            toolbar: DocTableToolbar,
                        }}
                        slotProps={{
                            /** Context menu. */
                            row: { onContextMenu: handleContextMenu },

                            basePopper: {
                                sx: {
                                    '& .MuiDataGrid-columnsManagement, .MuiDataGrid-columnsManagementFooter':
                                        {
                                            '& .MuiFormControlLabel-root > div': {
                                                display: 'none',
                                            },
                                        },
                                },
                            },
                        }}
                        onCellDoubleClick={(_, e) => (e.defaultMuiPrevented = true)}
                        sx={{
                            '& .MuiDataGrid-main': { WebkitBorderTopLeftRadius: 4 },
                            flexGrow: 4,
                        }}
                        onRowSelectionModelChange={onSelectionChanged}
                        getDetailPanelContent={cols.length ? getAdditionalDocuments : null}
                        apiRef={gridApiRef}
                        loading={docsLoading}
                        columns={cols}
                        rows={rows}
                        rowSelectionModel={rows
                            .filter(x => selectedDocuments.includes(x.UniversalID))
                            .map(y => y.NoteId)}
                        onPreferencePanelClose={({ openedPanelValue }) => {
                            openedPanelValue === 'filters' && dispatch(setShowFilter(false));
                            openedPanelValue === 'columns' && dispatch(setShowColumnsMenu(false));
                        }}
                        initialState={{
                            columns: { columnVisibilityModel },
                        }}
                        onFilterModelChange={newFilterModel =>
                            dispatch(
                                setHasFilteredItems(newFilterModel.items.some(item => item.value)),
                            )
                        }
                        fieldsWithMultilineType={fieldsWithMultilineType}
                    />
                </ErrorBoundary>

                <DocTableMenu
                    docType={selectedDocumentDocType}
                    universalId={selectedDocumentUniversalId}
                    contextMenuPos={contextMenuPosition}
                    setContextMenu={setContextMenuPosition}
                    currentDocument={currentDocument}
                />
                <Dropzone containerRef={divRef} onDropFiles={onDropFiles} />
            </div>
            {showUploadDialog && <UploadDialog onClose={onUploadDialogClose} />}
        </div>
    );
};
